import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Tag,
  Space,
  Tooltip,
  message,
  Checkbox,
  Card,
  Statistic,
  Row,
  Col,
  Typography,
  Badge,
  Popconfirm,
  Select,
  Input,
  Alert
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  CheckOutlined,
  UndoOutlined,
  FileTextOutlined,
  FilterOutlined
} from '@ant-design/icons';
import validationService from '../../../../services/validationService';
import { formatCurrency } from '../../utils';
import { useAuth } from '../../../../context/AuthContext';
import { useSelection } from '../../../../SelectionContext';
import useBusinessResource from '../../../../hooks/useBusinessResource';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateBusinessResources } from '../../../../utils/businessResourceFreshness';
import {
  ValidationResult,
  ValidationConstraint,
  ValidationState
} from '../../../../types/validation';

const { Text, Title } = Typography;
const { Option } = Select;
const { Search } = Input;

interface TableauCalculsValidationProps {
  periodId: number;
  companyId: number;
  onValidationChange?: () => void;
  onShowValidationModal?: (calculation: any) => void;
  onShowBatchModal?: (selectedIds: number[]) => void;
  onShowReport?: () => void;
}

const TableauCalculsValidation: React.FC<TableauCalculsValidationProps> = ({
  periodId,
  companyId,
  onValidationChange,
  onShowValidationModal,
  onShowBatchModal,
  onShowReport
}) => {
  const [state, setState] = useState<ValidationState>({
    isValidating: false,
    selectedCalculations: [],
    validationResults: [],
    validationReport: null,
    constraints: [],
    showValidationModal: false,
    showBatchValidationModal: false,
    showReportModal: false
  });

  const [calculations, setCalculations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [statistics, setStatistics] = useState({
    total_calculations: 0,
    validated_calculations: 0,
    pending_validation: 0,
    validation_errors: 0,
    validation_warnings: 0
  });

  const { user, token } = useAuth();
  const { selectedCompany } = useSelection();
  const client = useQueryClient();
  const scope = { userId: user?.id, companyId, warehouseId: "company", moduleScope: "hr" };
  const [dataError, setDataError] = useState<string | null>(null);
  const resource = useBusinessResource({
    ...scope, resource: 'payroll-validation', parameters: [periodId], active: true,
    enabled: Boolean(token && periodId && Number(companyId) === Number(selectedCompany)),
    load: async signal => {
      const read = async (suffix: string) => {
        const response = await fetch(`/api/hr/payroll-processing/periods/${periodId}/${suffix}`, {
          signal, headers: { Authorization: `Bearer ${token}`, 'X-Company-Id': String(companyId) },
        });
        const result = await response.json();
        if (!response.ok || result.success === false) throw new Error(result.error || result.message || 'Lecture des validations impossible');
        return result.data || result;
      };
      const [calculations, statistics] = await Promise.all([read('calculations?include_validation=true'), read('validation-stats')]);
      if (!Array.isArray(calculations) || typeof statistics !== 'object') throw new Error('Réponse des validations invalide');
      return { calculations, statistics };
    },
  });
  const canMutate = resource.status === 'verified' && !dataError;
  useEffect(() => {
    setCalculations([]); setStatistics({ total_calculations: 0, validated_calculations: 0, pending_validation: 0, validation_errors: 0, validation_warnings: 0 });
    setState(previous => ({ ...previous, selectedCalculations: [], validationResults: [] }));
    setSearchText(''); setStatusFilter('all'); setDataError(null);
  }, [periodId, companyId, user?.id, selectedCompany]);
  useEffect(() => {
    setLoading(resource.loading);
    if (Number(companyId) !== Number(selectedCompany)) { setDataError('L’entreprise sélectionnée a changé. Rouvrez la validation.'); return; }
    if (resource.error) { setDataError(resource.error instanceof Error ? resource.error.message : 'Lecture des validations impossible'); return; }
    if (resource.status === 'verified' && resource.data) {
      setCalculations(resource.data.calculations); setStatistics(resource.data.statistics); setDataError(null);
      setState(previous => ({ ...previous, selectedCalculations: previous.selectedCalculations.filter(id => resource.data!.calculations.some(calculation => Number(calculation.id) === Number(id) && !calculation.is_validated)) }));
    }
  }, [companyId, selectedCompany, resource.data, resource.status, resource.error, resource.loading]);
  const refreshData = async () => {
    await invalidateBusinessResources(client, scope, 'payroll-calculation');
    await resource.refresh({ force: true });
  };

  const handleValidateCalculation = async (calculationId: number) => {
    try {
      if (!canMutate) throw new Error(dataError || 'Actualisez les validations avant toute action');
      setState(prev => ({ ...prev, isValidating: true }));
      await validationService.validateCalculation(calculationId);
      message.success('Calcul validé avec succès');
      await refreshData();
      onValidationChange?.();
    } catch (error) {
      console.error('Erreur lors de la validation:', error);
      message.error('Erreur lors de la validation du calcul');
    } finally {
      setState(prev => ({ ...prev, isValidating: false }));
    }
  };

  const handleUnvalidateCalculation = async (calculationId: number) => {
    try {
      if (!canMutate) throw new Error(dataError || 'Actualisez les validations avant toute action');
      setState(prev => ({ ...prev, isValidating: true }));
      await validationService.unvalidateCalculation(calculationId);
      message.success('Validation annulée avec succès');
      await refreshData();
      onValidationChange?.();
    } catch (error) {
      console.error('Erreur lors de l\'annulation:', error);
      message.error('Erreur lors de l\'annulation de la validation');
    } finally {
      setState(prev => ({ ...prev, isValidating: false }));
    }
  };

  const getValidationStatusTag = (calculation: any) => {
    if (calculation.is_validated) {
      return <Tag color="success" icon={<CheckCircleOutlined />}>Validé</Tag>;
    }
    if (calculation.validation_errors && calculation.validation_errors.length > 0) {
      return <Tag color="error" icon={<CloseCircleOutlined />}>Erreurs</Tag>;
    }
    if (calculation.validation_warnings && calculation.validation_warnings.length > 0) {
      return <Tag color="warning" icon={<ExclamationCircleOutlined />}>Avertissements</Tag>;
    }
    return <Tag color="default">En attente</Tag>;
  };

  const filteredCalculations = calculations.filter(calc => {
    const matchesSearch = calc.employee_name?.toLowerCase().includes(searchText.toLowerCase()) ||
                         calc.employee_code?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'validated' && calc.is_validated) ||
                         (statusFilter === 'pending' && !calc.is_validated && (!calc.validation_errors || calc.validation_errors.length === 0)) ||
                         (statusFilter === 'errors' && calc.validation_errors && calc.validation_errors.length > 0);

    return matchesSearch && matchesStatus;
  });

  const rowSelection = {
    selectedRowKeys: state.selectedCalculations,
    onChange: (selectedRowKeys: React.Key[]) => {
      setState(prev => ({ 
        ...prev, 
        selectedCalculations: selectedRowKeys as number[] 
      }));
    },
    getCheckboxProps: (record: any) => ({
      disabled: record.is_validated || !canMutate,
    }),
  };

  const columns = [
    {
      title: 'Employé',
      dataIndex: 'employee_name',
      key: 'employee_name',
      render: (text: string, record: any) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.employee_code}
          </Text>
        </div>
      ),
      sorter: (a: any, b: any) => a.employee_name.localeCompare(b.employee_name),
    },
    {
      title: 'Département',
      dataIndex: 'department_name',
      key: 'department_name',
      filters: Array.from(new Set(calculations.map(c => c.department_name))).map(dept => ({
        text: dept,
        value: dept,
      })),
      onFilter: (value: any, record: any) => record.department_name === value,
    },
    {
      title: 'Salaire Brut',
      dataIndex: 'gross_salary',
      key: 'gross_salary',
      render: (amount: number) => (
        <Text strong style={{ color: '#1890ff' }}>
          {formatCurrency(amount || 0)}
        </Text>
      ),
      sorter: (a: any, b: any) => (a.gross_salary || 0) - (b.gross_salary || 0),
    },
    {
      title: 'Salaire Net',
      dataIndex: 'net_salary',
      key: 'net_salary',
      render: (amount: number) => (
        <Text strong style={{ color: '#52c41a' }}>
          {formatCurrency(amount || 0)}
        </Text>
      ),
      sorter: (a: any, b: any) => (a.net_salary || 0) - (b.net_salary || 0),
    },
    {
      title: 'Statut Validation',
      key: 'validation_status',
      render: (_: any, record: any) => getValidationStatusTag(record),
      filters: [
        { text: 'Validé', value: 'validated' },
        { text: 'En attente', value: 'pending' },
        { text: 'Avec erreurs', value: 'errors' },
      ],
      onFilter: (value: any, record: any) => {
        if (value === 'validated') return record.is_validated;
        if (value === 'pending') return !record.is_validated && (!record.validation_errors || record.validation_errors.length === 0);
        if (value === 'errors') return record.validation_errors && record.validation_errors.length > 0;
        return true;
      },
    },
    {
      title: 'Problèmes',
      key: 'issues',
      render: (_: any, record: any) => {
        const errorCount = record.validation_errors?.length || 0;
        const warningCount = record.validation_warnings?.length || 0;
        
        if (errorCount === 0 && warningCount === 0) {
          return <Text type="secondary">Aucun</Text>;
        }

        return (
          <Space>
            {errorCount > 0 && (
              <Badge count={errorCount} style={{ backgroundColor: '#ff4d4f' }}>
                <Tag color="error">Erreurs</Tag>
              </Badge>
            )}
            {warningCount > 0 && (
              <Badge count={warningCount} style={{ backgroundColor: '#faad14' }}>
                <Tag color="warning">Avertissements</Tag>
              </Badge>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          <Tooltip title="Voir les détails">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => onShowValidationModal?.(record)}
            />
          </Tooltip>

          {!record.is_validated ? (
            <Tooltip title="Valider">
              <Popconfirm
                title="Valider ce calcul ?"
                onConfirm={() => handleValidateCalculation(record.id)}
                okText="Valider"
                cancelText="Annuler"
              >
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  style={{ color: '#52c41a' }}
                  loading={state.isValidating}
                  disabled={!canMutate}
                />
              </Popconfirm>
            </Tooltip>
          ) : (
            <Tooltip title="Annuler la validation">
              <Popconfirm
                title="Annuler la validation de ce calcul ?"
                onConfirm={() => handleUnvalidateCalculation(record.id)}
                okText="Annuler"
                cancelText="Non"
              >
                <Button
                  type="text"
                  icon={<UndoOutlined />}
                  style={{ color: '#fa8c16' }}
                  loading={state.isValidating}
                  disabled={!canMutate}
                />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {dataError && <Alert type="error" showIcon message="Validation non vérifiée" description={dataError}
        action={<Button onClick={() => { void resource.refresh({ force: true }).catch(() => {}); }}>Actualiser</Button>} style={{ marginBottom: 16 }} />}

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Calculs"
              value={statistics.total_calculations}
              prefix={<FileTextOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Validés"
              value={statistics.validated_calculations}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="En Attente"
              value={statistics.pending_validation}
              prefix={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avec Erreurs"
              value={statistics.validation_errors}
              prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtres et actions */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 8]} align="middle">
          <Col xs={24} sm={8}>
            <Search
              placeholder="Rechercher un employé..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              placeholder="Filtrer par statut"
            >
              <Option value="all">Tous les statuts</Option>
              <Option value="validated">Validés</Option>
              <Option value="pending">En attente</Option>
              <Option value="errors">Avec erreurs</Option>
            </Select>
          </Col>
          <Col xs={24} sm={10}>
            <Space>
              {state.selectedCalculations.length > 0 && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={() => onShowBatchModal?.(state.selectedCalculations)}
                  disabled={!canMutate}
                >
                  Valider la sélection ({state.selectedCalculations.length})
                </Button>
              )}
              <Button
                icon={<FileTextOutlined />}
                onClick={onShowReport}
              >
                Rapport de validation
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tableau */}
      <Card>
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredCalculations}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} calculs`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default TableauCalculsValidation;